import { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  ADMIN_TOKEN_KEY,
  clearAdminToken,
  getAdminToken,
  loginAdmin,
} from "../utils/adminAuth";

interface AdminAuthContextValue {
  isAuthenticated: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => getAdminToken() !== null);

  const login = useCallback(async (password: string) => {
    try {
      const ok = await loginAdmin(password);
      if (ok) {
        setIsAuthenticated(true);
      }
      return ok;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    clearAdminToken();
    setIsAuthenticated(false);
  }, []);

  const value = useMemo(
    () => ({ isAuthenticated, login, logout }),
    [isAuthenticated, login, logout]
  );

  return (
    <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
  );
}

function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
}

export { AdminAuthProvider, useAdminAuth, ADMIN_TOKEN_KEY };
